import type { EntityFilterSchema, FilterEnumValue, FilterField } from '@parcel/filters';

export type DynamicOptions = Record<string, readonly FilterEnumValue[]>;

export interface FilterValueMatch {
  field: FilterField;
  option: FilterEnumValue;
}

export interface FilterMenuModel {
  /** Empty-input view: recently used fields (else the first 3). */
  suggestedFields: readonly FilterField[];
  /** Empty-input view: every other filterable field. */
  allFields: readonly FilterField[];
  /** Typing view: fields whose label/key matches the input. */
  fieldMatches: readonly FilterField[];
  /** Typing view: enum/dynamic values matching the input, e.g. "cam" → City → Cambridge. */
  valueMatches: readonly FilterValueMatch[];
  /** Trimmed input for the "Search for '…'" row; null when input is empty. */
  searchText: string | null;
}

const SUGGESTED_FALLBACK = 3;
const VALUE_MATCH_CAP = 6;
const RECENT_LIMIT = 3;

export function filterableFields(schema: Pick<EntityFilterSchema, 'fields'>): FilterField[] {
  return schema.fields.filter((f) => f.type !== 'text');
}

export function fieldOptions(
  field: FilterField,
  dynamicOptions: DynamicOptions = {},
): readonly FilterEnumValue[] {
  // A workspace may rename an enum's labels (score buckets); the keys never change.
  if (field.type === 'enum') return dynamicOptions[field.key] ?? field.values;
  if (field.type === 'dynamic-enum' || field.type === 'entity') {
    return dynamicOptions[field.key] ?? [];
  }
  return [];
}

export function deriveFilterMenu(
  input: string,
  schema: Pick<EntityFilterSchema, 'fields'>,
  opts: { dynamicOptions?: DynamicOptions; recentKeys?: readonly string[] } = {},
): FilterMenuModel {
  const fields = filterableFields(schema);
  const trimmed = input.trim();
  const text = trimmed.toLowerCase();

  if (!text) {
    const recent = (opts.recentKeys ?? [])
      .map((key) => fields.find((f) => f.key === key))
      .filter((f): f is FilterField => Boolean(f));
    const suggestedFields = recent.length > 0 ? recent : fields.slice(0, SUGGESTED_FALLBACK);
    const allFields = fields.filter((f) => !suggestedFields.includes(f));
    return { suggestedFields, allFields, fieldMatches: [], valueMatches: [], searchText: null };
  }

  const fieldMatches = fields.filter(
    (f) => f.label.toLowerCase().includes(text) || f.key.toLowerCase().includes(text),
  );
  const valueMatches: FilterValueMatch[] = [];
  for (const field of fields) {
    for (const option of fieldOptions(field, opts.dynamicOptions)) {
      if (valueMatches.length >= VALUE_MATCH_CAP) break;
      if (option.label.toLowerCase().includes(text) || option.value.toLowerCase().includes(text)) {
        valueMatches.push({ field, option });
      }
    }
    if (valueMatches.length >= VALUE_MATCH_CAP) break;
  }
  return {
    suggestedFields: [],
    allFields: [],
    fieldMatches,
    valueMatches,
    searchText: trimmed,
  };
}

// -- Date presets --------------------------------------------------------------

export type DatePresetKey = 'this_year' | 'last_90' | 'last_30';

export const DATE_PRESETS: ReadonlyArray<{ key: DatePresetKey; label: string }> = [
  { key: 'this_year', label: 'This year' },
  { key: 'last_90', label: 'Last 90 days' },
  { key: 'last_30', label: 'Last 30 days' },
];

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function presetRange(key: DatePresetKey, today: Date): { after: string } {
  if (key === 'this_year') return { after: `${today.getFullYear()}-01-01` };
  const days = key === 'last_90' ? 90 : 30;
  const d = new Date(today);
  d.setDate(d.getDate() - days);
  return { after: toIsoDate(d) };
}

// -- Recently used fields (localStorage, per table) ----------------------------

function recentsKey(tableId: string) {
  return `parcel.filter-recents.${tableId}`;
}

export function readRecentFields(storage: Pick<Storage, 'getItem'>, tableId: string): string[] {
  try {
    const raw = storage.getItem(recentsKey(tableId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? [...new Set(parsed.filter((v): v is string => typeof v === 'string'))].slice(
          0,
          RECENT_LIMIT,
        )
      : [];
  } catch {
    return [];
  }
}

export function pushRecentField(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  tableId: string,
  fieldKey: string,
): void {
  try {
    const next = [fieldKey, ...readRecentFields(storage, tableId).filter((k) => k !== fieldKey)];
    storage.setItem(recentsKey(tableId), JSON.stringify(next.slice(0, RECENT_LIMIT)));
  } catch {
    // storage unavailable (private mode etc.) — recents are best-effort
  }
}

// -- Record typeahead matching ---------------------------------------------------

export interface RecordMatch {
  id: string;
  label: string;
  sublabel?: string;
}

const RECORD_MATCH_CAP = 5;

export function matchRecords(
  records: readonly RecordMatch[],
  input: string,
  cap = RECORD_MATCH_CAP,
): RecordMatch[] {
  const text = input.trim().toLowerCase();
  if (!text) return [];
  const out: RecordMatch[] = [];
  for (const record of records) {
    if (out.length >= cap) break;
    // label and sublabel are joined with a space so a query may span the boundary (intentional for typeahead).
    const haystack = `${record.label} ${record.sublabel ?? ''}`.toLowerCase();
    if (haystack.includes(text)) out.push(record);
  }
  return out;
}
